/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-24" data-testid="privacy-page">
      <Link to="/" className="btn-ghost text-xs mb-8 inline-flex" data-testid="privacy-back">
        <ArrowLeft size={12} /> Back Home
      </Link>
      <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-2">
        <Shield size={12} /> ▮ LEGAL
      </div>
      <h1 className="font-display text-4xl lg:text-5xl leading-none mb-3">Privacy Policy</h1>
      <p className="font-mono-ui text-[0.7rem] uppercase tracking-[0.2em] text-[var(--ff-text-dim)] mb-12">
        Last updated: February 26, 2026 · Effective date: February 26, 2026
      </p>

      <div className="prose-legal space-y-10">
        <Section n="1" title="Who We Are">
          <p>
            Freshy Fresh Productions LLC ("<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>") operates
            freshyfreshproductionsllc.net (the "Site"), the Freshy Fresh Discord server, and a Discord bot.
            This Policy explains what personal information we collect, why we collect it, how we use it,
            and the rights you have over it.
          </p>
          <p>
            This Policy sits alongside our <Link to="/terms" className="underline">Terms of Service</Link>. If
            anything here conflicts with the Terms, this Policy controls for privacy questions.
          </p>
        </Section>

        <Section n="2" title="Information We Collect from Discord">
          <p>
            When you sign in with Discord, we ask Discord for the following pieces of information via
            OAuth. You see the full list on Discord's consent screen before you approve:
          </p>
          <ul>
            <li><strong>Discord ID (snowflake).</strong> A permanent numeric ID Discord assigns you.</li>
            <li><strong>Username and display name.</strong> Your public Discord handle.</li>
            <li><strong>Avatar URL.</strong> Your Discord avatar image.</li>
            <li><strong>Email address</strong> (if you granted Discord permission to share it).</li>
            <li>
              <strong>The full list of Discord servers ("guilds") you belong to.</strong> This is essential for
              our safety system — see Section 5 below.
            </li>
            <li>
              <strong>Your Discord OAuth access &amp; refresh tokens.</strong> We store these so we can (a)
              refresh your role list without asking you to re-sign-in, (b) add the Members role to your
              Discord profile, and (c) auto-rejoin you to our server if we approve your ban appeal.
            </li>
            <li>
              <strong>Your Discord roles inside our server.</strong> We fetch these from Discord after sign-in
              to map you to a site role (Founder / Admin / Moderator / Employee / Host / User).
            </li>
          </ul>
          <p>
            We do <strong>not</strong> receive your Discord password, direct messages you send to other users,
            or your friends list. Discord does not expose those to third-party applications.
          </p>
        </Section>

        <Section n="3" title="Information You Give Us Directly">
          <ul>
            <li>Show ratings and feedback you leave on show pages.</li>
            <li>Job applications you submit through the Careers page (name, contact, resume/portfolio,
              answers you provide).</li>
            <li>HR case submissions (subject, description, and any evidence you attach).</li>
            <li>Ban appeal statements you write.</li>
            <li>Profile customizations (display name, bio, notification preferences).</li>
            <li>Payment information you enter into Stripe's checkout form. Stripe processes and stores it;
              we only see the last four digits of your card and a token.</li>
          </ul>
        </Section>

        <Section n="4" title="Information We Collect Automatically">
          <ul>
            <li>Standard server logs (IP address, timestamp, path, user agent) retained for up to 30 days
              for security and debugging.</li>
            <li>Session cookies / a JWT stored in your browser's local storage so you stay signed in.</li>
            <li>Feature-usage counters that never contain your identity (e.g. "how many people viewed
              this show's page today").</li>
          </ul>
          <p>We do not use third-party advertising cookies or ad-network tracking pixels.</p>
        </Section>

        <Section n="5" title="How the Blacklist &amp; Discord Account Data Are Used">
          <p>
            This is the section that matters most to Discord users, so we're being extremely direct here.
          </p>
          <ul>
            <li>
              <strong>We store your list of Discord servers</strong> because our safety system checks it
              against a maintained list of Discord servers we've blacklisted (usually because their communities
              have targeted our hosts or shows in the past).
            </li>
            <li>
              <strong>Every user's guild list is checked automatically</strong> on sign-in, every 6 hours in
              the background sync job, and whenever you press "Refresh roles" or the blacklist is updated.
            </li>
            <li>
              <strong>Guild-adjacency rule:</strong> in addition to the server blacklist, we keep a list of
              <em> individual</em> Discord users we've blacklisted. If you share <em>any</em> Discord server
              with a blacklisted user, our bot may auto-ban you from ours. This is called the "adjacency
              rule" — it is the widest net a Discord bot is technically capable of. It is <strong>not</strong>
              a claim that you are a friend of that user — Discord does not expose friend lists to bots. It
              only means the two accounts co-exist in at least one shared server.
            </li>
            <li>
              <strong>What happens when the rule fires:</strong> our bot sends you a DM with the reason, a
              link to appeal, and a bans your Discord user from our guild. Your Site account is flagged
              banned so paid features and moderation-restricted features are unavailable.
            </li>
            <li>
              <strong>Your data on the blacklisted servers stays on those servers.</strong> We never store
              or read the content of any server other than our own. We only compare guild IDs (numeric
              strings) to our maintained list.
            </li>
            <li>
              <strong>Appeals</strong> are reviewed by a human moderator. Approved appeals restore Site
              access, lift the Discord ban, and (if we still have a valid OAuth refresh token) auto-rejoin
              you to our Discord server.
            </li>
          </ul>
          <p>
            If you do not want us to use your Discord guild list this way, do not sign in with Discord.
            Fan features that require Discord (memberships, verification, watchlist reminders) will be
            unavailable, but you can still browse the Site anonymously.
          </p>
        </Section>

        <Section n="6" title="How We Use Your Information">
          <ul>
            <li>To create and maintain your account and provide the Services.</li>
            <li>To process membership payments, compute host payouts, and record earnings ledgers.</li>
            <li>To send critical DMs (ban notices, appeal outcomes, verification codes, membership
              confirmations, warnings).</li>
            <li>To enforce the moderation and blacklist system described in Section 5.</li>
            <li>To personalize your experience (watchlist, preferred timezone, notification preferences).</li>
            <li>To detect and prevent fraud, abuse, and security incidents.</li>
            <li>To comply with legal obligations (e.g. tax records for host payouts).</li>
          </ul>
        </Section>

        <Section n="7" title="Sharing &amp; Third Parties">
          <p>We share personal information only in these situations:</p>
          <ul>
            <li>
              <strong>Discord Inc.</strong> — We call Discord's API to fetch your identity, guild list, and
              roles, and to send DMs, register slash commands, and manage bans. What we send Discord is
              limited to what's necessary for those API calls.
            </li>
            <li>
              <strong>Stripe, Inc.</strong> — For payment processing and host payouts. Stripe collects card
              data directly; we receive tokens and transaction metadata.
            </li>
            <li>
              <strong>Anthropic (Claude Sonnet 4.5)</strong> — For our AI Assistant. Your question text is
              sent to Anthropic to generate a response. Freshy AI conversations are retained on our servers
              for up to 24 hours to provide conversational context.
            </li>
            <li>
              <strong>Emergent Platform</strong> — Our infrastructure provider (hosting, object storage,
              managed LLM API key).
            </li>
            <li>
              <strong>Law enforcement or a court order</strong> — Only when compelled by valid legal process.
            </li>
          </ul>
          <p>We do <strong>not</strong> sell personal information. We do <strong>not</strong> share your
            data with advertisers.</p>
        </Section>

        <Section n="8" title="Data Retention">
          <ul>
            <li>Your account &amp; profile data: for as long as your account is active.</li>
            <li>Discord OAuth tokens: refreshed as needed while your account is active; revoked upon
              unlink.</li>
            <li>Ban logs &amp; appeal records: retained indefinitely for moderation continuity.</li>
            <li>Server / access logs: up to 30 days.</li>
            <li>Freshy AI chat history: up to 24 hours per session.</li>
            <li>Payment records: as long as required by tax and financial-record laws (typically 7 years in
              the U.S.).</li>
          </ul>
        </Section>

        <Section n="9" title="Your Rights &amp; Choices">
          <ul>
            <li>
              <strong>Access:</strong> You can see everything we have about you on your Profile page. Request
              a full export via legal@freshyfreshproductionsllc.net.
            </li>
            <li>
              <strong>Correction:</strong> Update your display name, bio, and preferences from Profile at any
              time.
            </li>
            <li>
              <strong>Deletion:</strong> Delete your Site account from Profile. Deletion removes your Site
              profile and Discord OAuth tokens; it does not remove content already published by moderators
              (e.g. HR disclosures with your name in them) unless legally required.
            </li>
            <li>
              <strong>Unlink Discord / Re-link:</strong> Available from Profile. Re-linking resets verification.
            </li>
            <li>
              <strong>Opt out of DMs:</strong> Turn off notification preferences from Profile, or block the
              Freshy Fresh bot on Discord.
            </li>
            <li>
              <strong>Region-specific rights:</strong> If you're in the EEA, UK, California, or another
              jurisdiction with additional data-subject rights, contact us and we will honor requests
              consistent with those laws (right to portability, right to object, right to lodge a complaint
              with your DPA).
            </li>
          </ul>
        </Section>

        <Section n="10" title="Security">
          <p>
            We use TLS for all traffic, hash passwords with bcrypt, and store sensitive fields (OAuth
            tokens, Stripe customer IDs) in a database not accessible from the public network. No system
            is perfect — if you become aware of a vulnerability, please email
            security@freshyfreshproductionsllc.net so we can address it.
          </p>
        </Section>

        <Section n="11" title="Children">
          <p>
            The Services are not directed to children under 13. We do not knowingly collect personal
            information from children under 13. If you believe a child has provided us with personal
            information, contact us and we will delete it.
          </p>
        </Section>

        <Section n="12" title="Cross-Border Data">
          <p>
            We host in the United States. If you're accessing the Services from outside the U.S., your data
            will be transferred to and processed in the U.S. under whatever safeguards our infrastructure
            providers have in place.
          </p>
        </Section>

        <Section n="13" title="Changes to This Policy">
          <p>
            When we materially change this Policy, we'll update the "Last updated" date at the top and, when
            practical, post a notice on the Site or via Discord. Continued use of the Services after changes
            take effect constitutes acceptance.
          </p>
        </Section>

        <Section n="14" title="Contact">
          <p>
            Privacy questions? Email <strong>privacy@freshyfreshproductionsllc.net</strong>. Security issues?
            Email <strong>security@freshyfreshproductionsllc.net</strong>. General legal:
            <strong> legal@freshyfreshproductionsllc.net</strong>.
          </p>
        </Section>

        <div className="pt-8 mt-8 border-t border-[var(--ff-border)] flex flex-wrap gap-4 items-center justify-between">
          <div className="font-mono-ui text-[0.7rem] uppercase tracking-[0.2em] text-[var(--ff-text-dim)]">
            © Freshy Fresh Productions LLC · All rights reserved
          </div>
          <Link to="/terms" className="btn-ghost text-xs" data-testid="privacy-to-terms">
            Read the Terms of Service →
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section data-testid={`privacy-section-${n}`}>
      <h2 className="font-display text-2xl mb-4 flex items-baseline gap-3">
        <span className="text-[var(--ff-red)] font-mono-ui text-sm tracking-[0.2em]">§{n}</span>
        <span dangerouslySetInnerHTML={{ __html: title }} />
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-[var(--ff-text)]">{children}</div>
    </section>
  );
}
